import Phaser from 'phaser';
import type { CharacterConfig, HairstyleKey, HelmetKey, OutfitPatternKey } from '../types';
import { HELMET_COVERAGE } from '../config/presets';

// Rider is drawn ~70 px tall, centered on the saddle anchor.
// y=0 is the saddle (rider's seat); negative y is up.
//
// Each layer adds a darker shadow tone and a lighter highlight tone derived
// from the user-picked color so customization still works but the result
// reads as shaded illustration rather than flat shapes.

export function makeRider(scene: Phaser.Scene, cfg: CharacterConfig): Phaser.GameObjects.Container {
  const c = scene.add.container(0, 0);

  const hairShade = darken(cfg.hairColor, 0.35);
  const skinShade = darken(cfg.skinTone, 0.18);
  const skinLight = lighten(cfg.skinTone, 0.15);
  const shirtShade = darken(cfg.outfitPrimary, 0.22);
  const shirtLight = lighten(cfg.outfitPrimary, 0.15);
  const pantsShade = darken(cfg.outfitSecondary, 0.25);

  const helmetCover = HELMET_COVERAGE[cfg.helmet];
  if (helmetCover.allowsBackHair) {
    drawBackHair(scene, c, cfg.hairstyle, cfg.hairColor, hairShade);
  }

  // Legs / breeches with shading (sit at the saddle)
  c.add(scene.add.rectangle(-7, 8, 9, 20, pantsShade));
  c.add(scene.add.rectangle(7, 8, 9, 20, pantsShade));
  c.add(scene.add.rectangle(-7, 7, 7, 18, cfg.outfitSecondary));
  c.add(scene.add.rectangle(7, 7, 7, 18, cfg.outfitSecondary));
  // Boots
  c.add(scene.add.rectangle(-7, 18, 10, 6, 0x2b1d0e));
  c.add(scene.add.rectangle(7, 18, 10, 6, 0x2b1d0e));

  // Torso (shirt with pattern overlay) — adds shaded back, body, and chest highlight
  drawTorso(scene, c, cfg.outfitPattern, cfg.outfitPrimary, cfg.outfitSecondary, shirtShade, shirtLight);

  // Arms with shading
  c.add(scene.add.rectangle(-13, -8, 7, 18, shirtShade));
  c.add(scene.add.rectangle(13, -8, 7, 18, shirtShade));
  c.add(scene.add.rectangle(-13, -9, 5, 16, cfg.outfitPrimary));
  c.add(scene.add.rectangle(13, -9, 5, 16, cfg.outfitPrimary));
  // Hands
  c.add(scene.add.circle(-13, 1, 3.5, skinShade));
  c.add(scene.add.circle(13, 1, 3.5, skinShade));
  c.add(scene.add.circle(-13, 0, 3, cfg.skinTone));
  c.add(scene.add.circle(13, 0, 3, cfg.skinTone));

  // Neck — narrower, with a subtle clavicle line.
  c.add(scene.add.rectangle(0, -18, 6, 8, skinShade));
  c.add(scene.add.rectangle(0, -19, 4, 6, cfg.skinTone));
  // Collar shadow (curved by stacking two thin ellipses)
  c.add(scene.add.ellipse(0, -15, 18, 3, shirtShade));

  // Head: oval skull (taller than wide), with a soft chin via overlapping ellipse.
  c.add(scene.add.ellipse(0, -28, 18, 22, skinShade));
  c.add(scene.add.ellipse(0, -28, 16, 20, cfg.skinTone));
  // Side highlight (light from upper-right)
  c.add(scene.add.ellipse(-3, -31, 10, 12, skinLight));

  // Cheekbones — gentle pink wash, smaller and farther apart.
  const cheek = lighten(0xff8888, -0.05);
  c.add(scene.add.ellipse(-5.5, -25, 3.5, 2, cheek));
  c.add(scene.add.ellipse(5.5, -25, 3.5, 2, cheek));

  // Eyebrows — slightly arched (2 small rectangles offset diagonally).
  c.add(scene.add.rectangle(-4, -31, 4, 1.2, hairShade));
  c.add(scene.add.rectangle(-3, -31.5, 3, 1, hairShade));
  c.add(scene.add.rectangle(4, -31, 4, 1.2, hairShade));
  c.add(scene.add.rectangle(3, -31.5, 3, 1, hairShade));

  // Eyes — almond-shaped (ellipse instead of circle), proper iris + highlight.
  c.add(scene.add.ellipse(-3.5, -28, 4.4, 3, 0xffffff));
  c.add(scene.add.ellipse(3.5, -28, 4.4, 3, 0xffffff));
  // Eye shadow (top eyelid casts a tiny shadow on sclera)
  c.add(scene.add.ellipse(-3.5, -28.6, 4, 1, darken(cfg.skinTone, 0.25)));
  c.add(scene.add.ellipse(3.5, -28.6, 4, 1, darken(cfg.skinTone, 0.25)));
  // Iris
  c.add(scene.add.circle(-3.5, -28, 1.4, cfg.eyeColor));
  c.add(scene.add.circle(3.5, -28, 1.4, cfg.eyeColor));
  // Pupil
  c.add(scene.add.circle(-3.5, -28, 0.8, 0x111111));
  c.add(scene.add.circle(3.5, -28, 0.8, 0x111111));
  // Catchlight
  c.add(scene.add.circle(-3, -28.6, 0.5, 0xffffff));
  c.add(scene.add.circle(4, -28.6, 0.5, 0xffffff));
  // Lash hint at outer corner
  c.add(scene.add.rectangle(-5.6, -29, 1.4, 0.6, hairShade));
  c.add(scene.add.rectangle(5.6, -29, 1.4, 0.6, hairShade));

  // Nose — bridge highlight only (no nostril dot)
  c.add(scene.add.rectangle(0, -26, 1.2, 3.5, skinLight));

  // Mouth — slight upper-lip dip + light pink highlight under
  const mouth = scene.add.graphics();
  mouth.lineStyle(1, 0x6b3a3a, 1);
  mouth.beginPath();
  mouth.moveTo(-2.4, -22);
  mouth.lineTo(0, -21.3);
  mouth.lineTo(2.4, -22);
  mouth.strokePath();
  c.add(mouth);
  c.add(scene.add.rectangle(0, -21, 3, 0.6, lighten(0xff8888, 0.1)));

  // Ears (small, behind the hair if helmet doesn't cover)
  c.add(scene.add.ellipse(-9, -28, 3, 5, skinShade));
  c.add(scene.add.ellipse(9, -28, 3, 5, skinShade));
  c.add(scene.add.ellipse(-9, -28, 1.5, 3, cfg.skinTone));
  c.add(scene.add.ellipse(9, -28, 1.5, 3, cfg.skinTone));

  // Top hair (under helmet most of the time, but front fringe shows for several styles)
  if (!helmetCover.hidesTop) {
    drawTopHair(scene, c, cfg.hairstyle, cfg.hairColor, hairShade);
  } else {
    drawFringe(scene, c, cfg.hairstyle, cfg.hairColor, hairShade);
  }

  // Helmet
  drawHelmet(scene, c, cfg.helmet, cfg.outfitPrimary);

  return c;
}

// ─────────── Color helpers ───────────

function darken(color: number, amount: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const f = 1 - amount;
  return (Math.round(r * f) << 16) | (Math.round(g * f) << 8) | Math.round(b * f);
}

function lighten(color: number, amount: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  return (
    (Math.round(r + (255 - r) * amount) << 16) |
    (Math.round(g + (255 - g) * amount) << 8) |
    Math.round(b + (255 - b) * amount)
  );
}

// ─────────── Hair ───────────

function drawBackHair(
  scene: Phaser.Scene,
  c: Phaser.GameObjects.Container,
  style: HairstyleKey,
  color: number,
  shade: number,
): void {
  switch (style) {
    case 'long': {
      // Soft, rounded falls of hair behind the head, tapering to the tips.
      c.add(scene.add.ellipse(0, -14, 22, 32, shade));
      c.add(scene.add.ellipse(0, -14, 19, 30, color));
      // Tip + side wisps
      c.add(scene.add.ellipse(0, 2, 17, 10, color));
      c.add(scene.add.ellipse(-7, 4, 5, 8, color));
      c.add(scene.add.ellipse(7, 4, 5, 8, color));
      // Strand highlights
      c.add(scene.add.ellipse(-5, -8, 1.6, 12, shade));
      c.add(scene.add.ellipse(5, -8, 1.6, 12, shade));
      break;
    }
    case 'ponytail': {
      // Curved teardrop ponytail.
      c.add(scene.add.ellipse(-11, -22, 11, 22, shade));
      c.add(scene.add.ellipse(-10, -22, 9, 20, color));
      c.add(scene.add.circle(-10, -33, 4, shade));
      c.add(scene.add.circle(-10, -33, 3, color));
      // Hair tie (small, dark)
      c.add(scene.add.ellipse(-10, -32, 8, 2.5, 0x6b3410));
      break;
    }
    case 'pigtails': {
      // Two soft side-bunches with curved tips.
      c.add(scene.add.ellipse(-11, -26, 9, 12, shade));
      c.add(scene.add.ellipse(11, -26, 9, 12, shade));
      c.add(scene.add.ellipse(-10, -26, 7, 10, color));
      c.add(scene.add.ellipse(10, -26, 7, 10, color));
      // Curl tip drop
      c.add(scene.add.circle(-12, -20, 3, color));
      c.add(scene.add.circle(12, -20, 3, color));
      // Ties
      c.add(scene.add.ellipse(-10, -30, 9, 2.5, 0x6b3410));
      c.add(scene.add.ellipse(10, -30, 9, 2.5, 0x6b3410));
      break;
    }
    case 'braid': {
      // Braid drapes over the right shoulder onto the chest, so we can see it.
      // Path goes: behind right ear → onto shoulder → down across the chest.
      const segs = [
        { x: 9, y: -22, r: 4.4, color: shade },
        { x: 11, y: -16, r: 4.2, color },
        { x: 12, y: -10, r: 4.0, color: shade },
        { x: 11, y: -4, r: 3.8, color },
        { x: 10, y: 2, r: 3.5, color: shade },
        { x: 9, y: 8, r: 3.2, color },
      ];
      for (const s of segs) c.add(scene.add.circle(s.x, s.y, s.r, s.color));
      // Tie at end of braid
      c.add(scene.add.ellipse(9, 12, 6, 2.5, 0x6b3410));
      break;
    }
    case 'curly':
      c.add(scene.add.circle(-9, -22, 6, shade));
      c.add(scene.add.circle(9, -22, 6, shade));
      c.add(scene.add.circle(0, -16, 6, shade));
      c.add(scene.add.circle(-8, -22, 5, color));
      c.add(scene.add.circle(8, -22, 5, color));
      c.add(scene.add.circle(0, -16, 5, color));
      break;
    case 'bun': {
      // Low bun sitting at the nape of the neck (behind/around y = -18),
      // with a soft fall of hair draped over the right shoulder so it's
      // visible from the front.
      // The bun
      c.add(scene.add.circle(2, -18, 8, shade));
      c.add(scene.add.circle(2, -18, 7, color));
      // Bun swirl detail
      c.add(scene.add.circle(2, -18, 3, shade));
      c.add(scene.add.circle(0, -19, 1.5, color));
      // Hair pulled back along the head from the top down to the bun
      c.add(scene.add.ellipse(0, -28, 18, 14, color));
      // Drape over the right shoulder
      const segs = [
        { x: 7, y: -14, r: 4.5, color: shade },
        { x: 9, y: -10, r: 4.2, color },
        { x: 10, y: -5, r: 4.0, color: shade },
        { x: 11, y: 0, r: 3.8, color },
        { x: 11, y: 5, r: 3.4, color: shade },
      ];
      for (const s of segs) c.add(scene.add.circle(s.x, s.y, s.r, s.color));
      // Soft tip
      c.add(scene.add.ellipse(11, 9, 5, 3, color));
      break;
    }
    default:
      break;
  }
}

function drawTopHair(
  scene: Phaser.Scene,
  c: Phaser.GameObjects.Container,
  style: HairstyleKey,
  color: number,
  shade: number,
): void {
  drawFringe(scene, c, style, color, shade);
  switch (style) {
    case 'short':
    case 'bob':
      c.add(scene.add.ellipse(0, -34, 23, 11, shade));
      c.add(scene.add.ellipse(0, -34, 22, 10, color));
      break;
    case 'mohawk':
      // Tapered mohawk: wider at the head, pointing up.
      c.add(scene.add.ellipse(0, -38, 8, 16, shade));
      c.add(scene.add.ellipse(0, -38, 6, 14, color));
      c.add(scene.add.circle(0, -45, 2, shade));
      break;
    case 'buzz':
      c.add(scene.add.ellipse(0, -34, 18, 6, color));
      break;
    default:
      c.add(scene.add.ellipse(0, -34, 21, 11, shade));
      c.add(scene.add.ellipse(0, -34, 20, 10, color));
      break;
  }
}

function drawFringe(
  scene: Phaser.Scene,
  c: Phaser.GameObjects.Container,
  style: HairstyleKey,
  color: number,
  shade: number,
): void {
  switch (style) {
    case 'curly':
      c.add(scene.add.circle(-6, -32, 3, shade));
      c.add(scene.add.circle(0, -33, 3, shade));
      c.add(scene.add.circle(6, -32, 3, shade));
      c.add(scene.add.circle(-6, -32, 2.4, color));
      c.add(scene.add.circle(0, -33, 2.4, color));
      c.add(scene.add.circle(6, -32, 2.4, color));
      break;
    case 'mohawk':
      c.add(scene.add.ellipse(0, -34, 6, 4, color));
      break;
    case 'buzz':
      c.add(scene.add.ellipse(0, -33, 13, 1.5, shade));
      break;
    case 'pigtails':
    case 'ponytail':
    case 'long':
    case 'bob':
    case 'short':
    case 'braid':
    case 'bun':
    default:
      // Soft fringe sweep with shadow.
      c.add(scene.add.ellipse(-1, -32, 16, 5, shade));
      c.add(scene.add.ellipse(0, -33, 14, 3.5, color));
      // Side wisp curls
      c.add(scene.add.ellipse(-7, -31, 3, 5, color));
      c.add(scene.add.ellipse(7, -32, 3, 4, color));
      break;
  }
}

// ─────────── Torso / outfit ───────────

function drawTorso(
  scene: Phaser.Scene,
  c: Phaser.GameObjects.Container,
  pattern: OutfitPatternKey,
  primary: number,
  secondary: number,
  shade: number,
  light: number,
): void {
  // Shaded back
  c.add(scene.add.rectangle(0, -7, 24, 24, shade));
  // Base shirt
  c.add(scene.add.rectangle(0, -8, 22, 22, primary));
  // Chest highlight (lit from upper-right)
  c.add(scene.add.rectangle(-4, -10, 8, 12, light));
  // Vertical button strip down the centre, like a riding shirt
  c.add(scene.add.rectangle(0, -8, 1.5, 22, shade));
  c.add(scene.add.circle(0, -14, 0.9, light));
  c.add(scene.add.circle(0, -8, 0.9, light));
  c.add(scene.add.circle(0, -2, 0.9, light));

  switch (pattern) {
    case 'stripes': {
      const g = scene.add.graphics();
      g.fillStyle(secondary, 1);
      for (let y = -16; y <= 0; y += 5) {
        g.fillRect(-11, y, 22, 2);
      }
      c.add(g);
      break;
    }
    case 'polkadot': {
      const g = scene.add.graphics();
      g.fillStyle(secondary, 1);
      for (let y = -14; y <= 2; y += 6) {
        for (let x = -8; x <= 8; x += 6) {
          g.fillCircle(x, y, 1.4);
        }
      }
      c.add(g);
      break;
    }
    case 'plaid': {
      const g = scene.add.graphics();
      g.fillStyle(secondary, 0.6);
      for (let x = -10; x <= 10; x += 5) g.fillRect(x, -18, 1, 22);
      for (let y = -16; y <= 2; y += 5) g.fillRect(-11, y, 22, 1);
      c.add(g);
      break;
    }
    case 'solid':
    default:
      break;
  }

  // Belt
  c.add(scene.add.rectangle(0, 4, 22, 3, 0x2b1d0e));
  c.add(scene.add.rectangle(0, 4, 4, 4, 0xc7a64a));
}

// ─────────── Helmet ───────────

function drawHelmet(
  scene: Phaser.Scene,
  c: Phaser.GameObjects.Container,
  helmet: HelmetKey,
  accent: number,
): void {
  // Helmets sit on top of the head (y ~ -38) so the eyes at y=-28 stay visible.
  const accentShade = darken(accent, 0.3);
  switch (helmet) {
    case 'none':
      return;
    case 'riding': {
      // Outer shell shadow + base + highlight
      c.add(scene.add.ellipse(0, -38, 22, 14, 0x111111));
      c.add(scene.add.ellipse(0, -38, 20, 12, 0x2c2c2c));
      c.add(scene.add.ellipse(-4, -40, 10, 4, 0x4a4a4a));
      // Brim
      c.add(scene.add.rectangle(5, -34, 9, 2.5, 0x111111));
      // Vent
      c.add(scene.add.rectangle(0, -44, 4, 4, 0x4a4a4a));
      c.add(scene.add.rectangle(0, -44, 3, 3, 0x666666));
      break;
    }
    case 'jockey': {
      c.add(scene.add.ellipse(0, -38, 22, 11, accentShade));
      c.add(scene.add.ellipse(0, -38, 20, 10, accent));
      c.add(scene.add.ellipse(-4, -40, 10, 3, lighten(accent, 0.2)));
      c.add(scene.add.rectangle(7, -34, 11, 2.5, accentShade));
      c.add(scene.add.rectangle(7, -34, 10, 2, accent));
      // Pom
      c.add(scene.add.circle(0, -45, 3, 0xffffff));
      c.add(scene.add.circle(-1, -46, 1.4, 0xeaeaea));
      break;
    }
    case 'cowboy': {
      // Brim shadow + base
      c.add(scene.add.ellipse(0, -32, 34, 6, 0x6b3410));
      c.add(scene.add.ellipse(0, -33, 32, 5, 0x8a5230));
      // Crown
      c.add(scene.add.ellipse(0, -39, 22, 11, 0x6b3410));
      c.add(scene.add.ellipse(0, -39, 20, 10, 0xa86a3a));
      c.add(scene.add.ellipse(-4, -41, 8, 3, 0xc4894a));
      // Hat band
      c.add(scene.add.rectangle(0, -36, 21, 2.5, 0x4a2510));
      // Buckle
      c.add(scene.add.rectangle(0, -36, 2.5, 2.5, 0xc7a64a));
      break;
    }
    case 'knight': {
      // Full helm covers the head from above the brows to just below the chin.
      // Outer (shadow) shell
      c.add(scene.add.ellipse(0, -28, 24, 28, 0x6a6f80));
      // Inner (light) shell
      c.add(scene.add.ellipse(0, -28, 22, 26, 0xb0b6c0));
      // Highlight on the upper-left
      c.add(scene.add.ellipse(-4, -33, 10, 6, 0xd6dae0));
      // Visor slit at eye height so eyes peek through
      c.add(scene.add.rectangle(0, -29, 17, 2.5, 0x111111));
      c.add(scene.add.rectangle(0, -29, 16, 1.5, 0x222222));
      // Rivets along the brow
      c.add(scene.add.circle(-9, -32, 0.9, 0x40454f));
      c.add(scene.add.circle(9, -32, 0.9, 0x40454f));
      // Chin guard line
      c.add(scene.add.rectangle(0, -19, 18, 1, 0x40454f));
      // Crest on top
      c.add(scene.add.rectangle(0, -45, 4, 8, accentShade));
      c.add(scene.add.rectangle(0, -45, 3, 7, accent));
      c.add(scene.add.triangle(0, -49, 0, 0, -6, 4, 6, 4, accent));
      break;
    }
  }
}
